<template>
    <b-row class="match-height">
        <b-col md="12">
            <b-overlay
                :show="status === 'loading'"
            >
                <MonthlyBarChart
                    :status="status"
                    :themeColors="themeColors"
                    :theme="theme"
                    :data="months"
                    :month="month"
                    @change:month="changeMonth"
                >
                </MonthlyBarChart>
            </b-overlay>
        </b-col>
        <b-col md="12">
            <b-overlay
                :show="status === 'loading'"
            >
                <EstimatedFullCard
                    :month="month"
                    :prevMonth="prevMonth"
                    :estimated="estimated"
                    :status="status"
                    :themeColors="themeColors"
                    :theme="theme"
                    :cumulativeCost="cumulativeCost"
                    :currentTrends="currentTrends"
                    :pastTrends="pastTrends"
                    :cumulativeCostStatus="cumulativeCostStatus"
                    :noData="noData"
                />
            </b-overlay>
        </b-col>
        <b-col md="12">
            <b-overlay
                :show="status === 'loading'"
            >
                <AccountsTable
                    :status="status"
                    :month="month"
                    :prevMonth="prevMonth"
                    :finalData="{ finalEstimatedData, finalPastData }"
                    :estimated="estimated"
                    :downloadStatus="downloadLoading"
                    @download:excel="downloadConsolidatedExcel"
                />
            </b-overlay>
        </b-col>
        <b-col md="6">
            <b-overlay
                :show="status === 'loading'"
            >
                <ProductCardTable
                    :status="status"
                    :data="products"
                    @click:more="clickMoreHandler"
                />
            </b-overlay>
        </b-col>
        <b-col md="6">
            <b-overlay
                :show="status === 'loading'"
            >
                <RegionCardTable
                    :status="status"
                    :data="regions"
                    :region="region"
                />
            </b-overlay>
        </b-col>
    </b-row>
</template>

<script>
import useCommonData from '@/composables/useCommonData'
import { getCurrentInstance, computed, inject, ref, toRefs, watch } from '@vue/composition-api'
import { FETCH_SELECTED_MONTH } from '@/store/common/mutation-type'
import MonthlyBarChart from './_components/MonthlyBarChart'
import EstimatedFullCard from './_components/EstimatedFullCard'
import AccountsTable from './_components/AccountsTable'
import ProductCardTable from './_components/ProductCardTable'
import RegionCardTable from './_components/RegionCardTable'
export default {
    name: 'AllConsolidatedModule',
    components: {
        MonthlyBarChart,
        EstimatedFullCard,
        AccountsTable,
        ProductCardTable,
        RegionCardTable
    },
    props: [
        'group',
        'project',
        'month',
        'estimated',
        'qtypeParam'
    ],
    setup (props, context) {
        const vm = getCurrentInstance().proxy
        const store = vm.$store
        const themeColors = inject('themeColors')
        const theme = inject('theme')

        const productCodes = ref([])
        const downloadLoading = computed(() => store.getters['$_allConsolidated/downloadLoading'])

        const { region, __enMonthMap } = useCommonData()
        const { group, project, month, estimated, qtypeParam } = toRefs(props)

        const status = computed(() => store.getters['$_allConsolidated/status'])
        const noData = computed(() => status.value === 204)
        const cumulativeCost = computed(() => store.getters['$_allConsolidated/cumulativeCost'])
        const currentTrends = computed(() => store.getters['$_allConsolidated/currentTrends'])
        const pastTrends = computed(() => store.getters['$_allConsolidated/pastTrends'])
        const finalEstimatedData = computed(() => store.getters['$_allConsolidated/finalEstimatedData'])
        const finalPastData = computed(() => store.getters['$_allConsolidated/finalPastData'])
        const months = computed(() => store.getters['$_allConsolidated/months'])
        const regions = computed(() => store.getters['$_allConsolidated/regions'])
        const products = computed(() => store.getters['$_allConsolidated/products'])
        const prevMonth = computed(() => getMonth(month.value, 'prev'))
        const cumulativeCostStatus = computed(() => (typeof cumulativeCost.value.currentMonthCost === 'number' && typeof cumulativeCost.value.prevMonthCost === 'number' && unCostCheck.value))
        const unCostCheck = computed(() => currentTrends.value.filter(item => item.unCost !== 0).length > 0)
        const changeProject = computed(() => store.getters.changeProject)

        const getMonth = (string, type) => {
            const arr = string.split('-')
            let year = parseInt(arr[0], 10)
            let month = parseInt(arr[1], 10)
            const convert = {
                next: function () {
                    month++
                    if (month === 13) {
                        month = 1
                        year++
                    }
                },
                prev: function () {
                    month--
                    if (month === 0) {
                        month = 12
                        year--
                    }
                }
            }
            convert[type]()
            return `${year}-${month < 10 ? '0' : ''}${month}`
        }
        const fetchAllConsolidated = (params) => store.dispatch('$_allConsolidated/fetchAllConsolidated', params)
        const changeMonth = ({ value }) => store.commit(FETCH_SELECTED_MONTH, value)
        const reloadAvailable = () => {
            fetchAllConsolidated({
                ...qtypeParam.value,
                month: month.value,
                estimated: estimated.value
            })
        }
        watch(changeProject, (value, oldValue) => {
            reloadAvailable()
        }, {
            immediate: false,
            deep: true
        })
        watch(month, (value, oldValue) => {
            reloadAvailable()
        }, {
            immediate: false,
            deep: true
        })
        const downloadConsolidatedExcel = () => {
            store.dispatch('$_allConsolidated/downloadConsolidatedAccountExcel', {
                ...qtypeParam.value,
                month: month.value,
                estimated: estimated.value
            }).then(data => {
                const fileURL = window.URL.createObjectURL(new Blob([data]))
                const fileLink = document.createElement('a')
                fileLink.href = fileURL
                const fileName = (qtypeParam.qtype === 'group' ? 'Group-' + group.value.groupName : qtypeParam.qtype === 'project' ? 'Project-' + project.value.projectName : 'All') + '-AWS-Consolidated.xlsx'
                fileLink.setAttribute('download', fileName)
                document.body.appendChild(fileLink)
                fileLink.click()
            })
        }
        const clickMoreHandler = () => {
            vm.$router.push({ name: 'consolidatedProduct', params: { products: products.value } })
        }
        return {
            region,
            __enMonthMap,
            themeColors,
            theme,
            productCodes,
            downloadLoading,
            status,
            noData,
            currentTrends,
            pastTrends,
            finalEstimatedData,
            finalPastData,
            months,
            regions,
            products,
            prevMonth,
            cumulativeCost,
            cumulativeCostStatus,
            downloadConsolidatedExcel,
            changeMonth,
            fetchAllConsolidated,
            clickMoreHandler
        }
    }
}
</script>

<style>

</style>
